/**
 * StaffCalendarTab — this person's external calendar connection.
 *
 * Data: bound — ops: getCalendarSyncProviders, getCalendarSyncConnection,
 * startCalendarSync, deleteCalendarSyncConnection (/v1/admin/calendar-sync/*),
 * through StaffCalendarConnection, the SAME component the retired /staff editor
 * mounts (built and observed in drop-2026-0921a-calendar-connect). This tab is
 * where it lives from now on; nothing about the flow is re-implemented.
 *
 * Google Calendar or Microsoft 365 Calendar (CC 0112). Both run on tenant-level
 * credentials (Google's service account; the Microsoft app registration), so there
 * is no per-person sign-in. A provider the tenant hasn't set up is shown disabled
 * with its reason and a link to Settings → Integrations.
 *
 * Gated like the /staff editor: the calendar-sync lane is integrations.read /
 * integrations.write, not team.write.
 */
import { Icon, IconTile, InlineEmptyState, Section, T } from "@v0y4ge/jrni-ui";
import { can } from "../../shell/access";
import { useT } from "../../lib/i18n";
import { StaffCalendarConnection } from "../settings/CalendarSyncPanels";

const PANEL: React.CSSProperties = { width: "100%", maxWidth: 880, display: "flex", flexDirection: "column", gap: 14 };

export function StaffCalendarTab({ staffId, email }: { staffId: string | null; email: string }) {
  const t = useT();
  const canRead = can("integrations.read");
  const canWrite = can("integrations.write");
  const title = "Calendar";
  const blurb = "Bookings with this person are written to their work calendar, and events already in it block their availability.";

  if (!staffId || !canRead) {
    return (
      <div style={PANEL}>
        <Section title={title} description={blurb}>
          <InlineEmptyState style={{ marginTop: 8 }}
            icon={<IconTile icon={!staffId ? <Icon.calendar size={16} /> : <Icon.lock size={16} />} />}
            title={!staffId ? t("staffAvailability.noRosterRecord", "No roster record") : t("staffCalendar.noAccess", "You can't view calendar connections")}
            description={!staffId
              ? t("staffCalendar.noRosterRecordBody", "A calendar connects to a roster record, and this person has a workspace membership only. Add them to the staff roster first.")
              : t("staffCalendar.noAccessBody", "Calendar connections are integration settings. Viewing them needs the Integrations read permission.")} />
        </Section>
      </div>
    );
  }

  /* No second "Calendar" heading (operator, 6 Oct): the page header already names the
     tab. Two facts say what a connection does, then one card per provider. */
  return (
    <div style={PANEL}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 10 }}>
        {[
          { icon: <Icon.calendar size={15} />, title: "Bookings go to their calendar", sub: "New and changed bookings with this person appear in the connected calendar." },
          { icon: <Icon.clock size={15} />, title: "Busy time blocks booking", sub: "Events already in that calendar are taken off this person's availability." },
        ].map((f) => (
          <div key={f.title} style={{ display: "flex", gap: 10, alignItems: "flex-start", padding: "12px 14px", borderRadius: 10, background: T.n6 }}>
            <IconTile icon={f.icon} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: T.n1 }}>{f.title}</div>
              <div style={{ fontSize: 12, color: T.soft, lineHeight: 1.5, marginTop: 2 }}>{f.sub}</div>
            </div>
          </div>
        ))}
      </div>
      <StaffCalendarConnection staffId={staffId} staffEmail={email} canWrite={canWrite} />
      <p style={{ margin: 0, fontSize: 12, color: T.soft, lineHeight: 1.5 }}>
        One calendar per provider. A person can connect Google and Microsoft 365 at the same time.
      </p>
    </div>
  );
}
